import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Skeleton,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CenterBox from '@/components/CenterBox/index.js';
import { useUserData } from '@/hooks/useUserData';
import { useToastError } from '@/hooks/useToastError';

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, py: 1.25, px: 2 }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ flexShrink: 0, width: 110 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={500}
      sx={{
        flex: 1,
        textAlign: 'right',
        color: value ? 'text.primary' : 'text.disabled',
        fontStyle: value ? 'normal' : 'italic',
        wordBreak: 'break-word',
      }}
    >
      {value || '–'}
    </Typography>
  </Box>
);

const UserInfo = () => {
  const { t } = useTranslation();

  const { data: user, loading, error } = useUserData();
  useToastError(error, t('userInfo.fetchError'));

  if (!user) return (
    <CenterBox>
      <Skeleton variant="rounded" height={48} sx={{ mb: 2.5, borderRadius: 3 }} />
      <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
    </CenterBox>
  );

  const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase();
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');

  return (
    <CenterBox>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 0.5, mb: 2.5, mt: 2 }}>
        {loading
          ? (
              <Skeleton variant="circular" width={48} height={48} />
            )
          : (
              <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontWeight: 700 }}>
                {initials}
              </Avatar>
            )}
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? (
                <>
                  <Skeleton width={140} height={20} />
                  <Skeleton width={90} height={16} sx={{ mt: 0.5 }} />
                </>
              )
            : (
                <>
                  <Typography variant="subtitle1" fontWeight={700} noWrap lineHeight={1.3}>
                    {fullName || user.loginname}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {user.loginname}
                  </Typography>
                </>
              )}
        </Box>
      </Box>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        }}
      >
        {loading
          ? (
              <>
                <Skeleton height={100} />
              </>
            )
          : (
              <>
                <InfoRow label={t('userInfo.firstName')} value={user.firstName} />
                <Divider sx={{ mx: 2 }} />
                <InfoRow label={t('userInfo.lastName')} value={user.lastName} />
                <Divider sx={{ mx: 2 }} />
                <InfoRow label={t('userInfo.email')} value={user.email} />
                <Divider sx={{ mx: 2 }} />
                <InfoRow label={t('userInfo.phone')} value={user.phone} />
                <Divider sx={{ mx: 2 }} />
                <InfoRow label={t('userInfo.address')} value={user.address} />
                <Divider sx={{ mx: 2 }} />
                <InfoRow label={t('userInfo.postalCode')} value={user.postalCode} />
                <Divider sx={{ mx: 2 }} />
                <InfoRow label={t('userInfo.city')} value={user.city} />
              </>
            )}
      </Box>
    </CenterBox>
  );
};

export default UserInfo;
